/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { ToastAction } from 'src/luis/types/stores/events/ToastAction';
import { ToastStatus } from 'src/luis/types/stores/events/ToastStatus';

export type ToastData = Partial<Omit<ToastStore, 'timestamp' | 'dismissed' | 'seen'>>;

/**
 * A store for toast data.
 *
 * @param id The id of the toast to create.
 * @param toast The toast data.
 */
export class ToastStore {
    public id: string;

    public quiet: boolean;

    public autoDismissDelay?: number;

    @observable public title: string;

    @observable public status: ToastStatus;

    @observable public dismissed: boolean;

    @observable public seen: boolean;

    @observable public timestamp: number;

    @observable public content: React.ReactNode;

    @observable public actions: ToastAction[];

    @observable public isTitleTruncated: boolean;

    @observable customIconName?: string;

    constructor(id: string, toast: ToastData) {
        this.id = id;
        this.title = toast.title ?? '';
        this.status = toast.status ?? 'running';
        this.dismissed = false;
        this.seen = false;
        this.timestamp = Date.now();
        this.content = toast.content ?? '';
        this.actions = toast.actions ?? [];
        this.quiet = toast.quiet;
        this.autoDismissDelay = toast.autoDismissDelay;
        this.customIconName = toast.customIconName;
        this.isTitleTruncated = toast.isTitleTruncated ?? true;
    }

    /**
     * Updates the dismissal state of failed toast
     * to re-appear and notify the user.
     */
    private _updateDissmissalState(newStatus: ToastStatus) {
        if (newStatus === 'failed' || newStatus === 'completed') {
            this.dismissed = false;
        }
    }

    /**
     * Updates the toast status.
     */
    @action
    public updateToastStatus(newStatus: ToastStatus) {
        this.status = newStatus;
        this._updateDissmissalState(newStatus);
    }

    /**
     * Updates the toast.
     *
     * @param newToast The new toast object.
     */
    @action
    public updateToast(newToast: ToastData) {
        if (newToast.actions) {
            this.actions = newToast.actions;
        }

        if (newToast.content) {
            this.content = newToast.content;
        }

        if (newToast.title) {
            this.title = newToast.title;
        }

        if (newToast.customIconName) {
            this.customIconName = newToast.customIconName;
        }

        if (newToast.status) {
            this.updateToastStatus(newToast.status);
        }
    }

    /**
     * Marks the toast as dismissed.
     */
    @action
    public markAsDismissed() {
        this.dismissed = true;
    }

    /**
     * Marks the toast as seen.
     */
    @action
    public markAsSeen() {
        this.seen = true;
    }
}
